import { useState } from "react";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useGetHrdCaseQuery } from "@/api/Hrd/HrdCaseApi.ts";
import Loader from "@/components/Loader.tsx";
import { ElementNotFound } from "@/components/errors/ElementNotFound.tsx";
import HrdCaseSummaryCard from "@/components/hrd/cases/HrdCaseSummaryCard.tsx";
import { Alert, AlertTitle, Button, Stack } from "@mui/material";
import AppCard from "@components/AppCard.tsx";
import HrdExploitation from "@components/hrd/cases/HrdExploitation.tsx";
import { requireRole } from "@/auth/requireRole.ts";
import { ROLE_ADMIN, ROLE_USER } from "@/constants";
import { getHasHrdAccess } from "@/auth/hasHrdAccess.tsx";
import { useCurrentUser } from "@/store/hooks.ts";
import DeleteButton from "@components/DeleteButton.tsx";

const PERMISSIONS = {
  CASE_STATUS: {
    CAN_EDIT: ["DRAFT"],
  },
};

export const Route = createFileRoute("/_dashboard/hrd/cas/$uuid")({
  beforeLoad: (args) => {
    const { context } = args;
    requireRole([ROLE_ADMIN, ROLE_USER])(args);
    const hasHrdAccess = getHasHrdAccess(
      context.session!.user!.hrdAccessStatus
    );
    // Check HRD access for non-admins only
    if (!context.session?.user?.isAdmin && !hasHrdAccess) {
      throw redirect({
        to: "/unauthorized",
        search: {
          redirect: location.href,
        },
      });
    }

    return { session: context.session! };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { uuid } = Route.useParams();
  const { data: hrdCase, isLoading } = useGetHrdCaseQuery(uuid);
  const user = useCurrentUser();
  const navigate = useNavigate();

  // État pour l'édition admin forcée sur cas validés
  const [adminEditEnabled, setAdminEditEnabled] = useState(false);

  const getPermissions = (caseStatus: string) => {
    const isStatusDraft = PERMISSIONS.CASE_STATUS.CAN_EDIT.includes(caseStatus);
    const hasActiveHrdAccess = user!.hrdAccessStatus === "ACTIVE";

    return {
      // Les admins peuvent toujours éditer, les autres seulement si DRAFT
      canEdit: (user!.isAdmin || isStatusDraft) && hasActiveHrdAccess,
      canDelete: user!.isAdmin,
      // Indique si l'admin édite un cas non-DRAFT (validé)
      isAdminForcingEdit: user!.isAdmin && !isStatusDraft,
    };
  };

  if (isLoading) return <Loader />;
  if (!hrdCase) return <ElementNotFound />;

  const { canEdit, canDelete, isAdminForcingEdit } = getPermissions(
    hrdCase.status.id
  );

  // Pour les admins sur cas validés, on attend la confirmation avant d'activer l'édition
  const effectiveCanEdit = isAdminForcingEdit ? adminEditEnabled : canEdit;

  const handleDeleteSuccess = () => {
    navigate({ to: "/hrd/cas" });
  };

  return (
    <Stack spacing={2}>
      <HrdCaseSummaryCard
        hrdCase={hrdCase}
        actions={
          canDelete && (
            <DeleteButton
              elementLabel={`le cas ${hrdCase.laboratoryUniqueReferenceEntry}`}
              elementIri={`hrd_cases/${hrdCase.uuid}`}
              onSuccess={handleDeleteSuccess}
              additionalText="Cette action supprimera définitivement ce cas HRD et toutes ses données associées. Cette opération est irréversible."
            />
          )
        }
      />

      {/* Warning pour les admins sur cas validés - avant activation */}
      {isAdminForcingEdit && !adminEditEnabled && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setAdminEditEnabled(true)}
            >
              Activer l'édition
            </Button>
          }
        >
          <AlertTitle>Cas validé - Édition restreinte</AlertTitle>
          Ce cas a été validé et est intégré aux statistiques. En tant
          qu'administrateur, vous pouvez forcer l'édition, mais cela peut
          impacter les statistiques globales. Cette action est réservée aux
          corrections exceptionnelles.
        </Alert>
      )}

      {/* Info persistante pendant l'édition admin */}
      {isAdminForcingEdit && adminEditEnabled && (
        <Alert severity="info">
          <AlertTitle>Mode édition administrateur</AlertTitle>
          Vous éditez un cas précédemment validé. L'enregistrement en brouillon
          repassera le statut à "Brouillon". La soumission le revalidera.
        </Alert>
      )}

      <AppCard>
        <HrdExploitation
          key={`${hrdCase.status.id}-${adminEditEnabled}`}
          hrdCase={hrdCase}
          asResume={!effectiveCanEdit}
        />
      </AppCard>
    </Stack>
  );
}
